/*
 * Recently viewed products.
 *
 * Records the handle of every product page the visitor opens in localStorage, then asks the
 * Section Rendering API to render sections/recently-viewed.liquid for those handles so the cards
 * are built by Dawn's own card-product snippet.
 *
 * Everything that touches localStorage is wrapped in try/catch: in private browsing, or when a
 * visitor has blocked storage, the section simply stays hidden.
 */
if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      static STORAGE_KEY = 'recently-viewed-products';
      static MAX_HISTORY = 20;

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.results = this.querySelector('[data-recently-viewed-results]');
        this.wrapper = this.closest('.recently-viewed-wrapper');

        const currentHandle = (this.dataset.productHandle || '').trim();
        const productsToShow = parseInt(this.dataset.productsToShow, 10) || 4;

        // Read the history before recording this page view so the current product is excluded.
        const history = this.readHistory().filter((handle) => handle !== currentHandle);
        this.remember(currentHandle);

        this.addEventListener('click', this.handleClick);

        if (this.results === null || history.length === 0) return;

        // A couple of spares cover products that have since been unpublished or deleted; the
        // section caps the rendered cards at products_to_show.
        this.schedule(() => this.loadProducts(history.slice(0, productsToShow + 2)));
      }

      /* -------------------------------------------------- rendering */

      loadProducts(handles) {
        const sectionId = this.dataset.sectionId;
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const url = `${root}search?section_id=${encodeURIComponent(sectionId)}&type=product&q=${encodeURIComponent(
          handles.join(' OR ')
        )}`;

        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`Recently viewed: request failed with ${response.status}`);
            return response.text();
          })
          .then((text) => {
            const rendered = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('[data-recently-viewed-results]');

            // No history match means no cards: leave the section hidden rather than show a
            // heading with nothing under it.
            if (rendered === null || rendered.querySelector('.grid__item') === null) return;

            this.results.innerHTML = rendered.innerHTML;
            this.reveal();
          })
          .catch(() => {
            // Silent by design: a failed request must never break the product page.
          });
      }

      reveal() {
        if (this.wrapper !== null) this.wrapper.hidden = false;
        this.classList.add('recently-viewed--loaded');
      }

      handleClick = (event) => {
        const button = event.target.closest('[data-recently-viewed-clear]');
        if (button === null) return;

        event.preventDefault();
        this.clearHistory();

        const status = document.createElement('p');
        status.className = 'recently-viewed__status';
        status.setAttribute('role', 'status');
        status.setAttribute('tabindex', '-1');
        status.textContent = button.dataset.clearedText || 'Your recently viewed products were cleared.';

        this.results.innerHTML = '';
        this.results.appendChild(status);
        // The cards the visitor was tabbing through are gone, so move focus to the confirmation.
        status.focus();
      };

      schedule(callback) {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(callback, { timeout: 1000 });
        } else {
          window.setTimeout(callback, 200);
        }
      }

      /* -------------------------------------------------- storage */

      get storageKey() {
        return this.constructor.STORAGE_KEY;
      }

      isValidHandle(value) {
        return typeof value === 'string' && value.length > 0 && value.length <= 255 && !/[\s/?&#"'<>]/.test(value);
      }

      readHistory() {
        try {
          const stored = window.localStorage.getItem(this.storageKey);
          if (!stored) return [];

          const parsed = JSON.parse(stored);
          if (!Array.isArray(parsed)) return [];

          const handles = [];
          parsed.forEach((entry) => {
            if (this.isValidHandle(entry) && !handles.includes(entry)) handles.push(entry);
          });

          return handles.slice(0, this.constructor.MAX_HISTORY);
        } catch (error) {
          // Unavailable or corrupt storage: behave as if there were no history.
          return [];
        }
      }

      remember(handle) {
        if (!this.isValidHandle(handle)) return;

        const history = [handle].concat(this.readHistory().filter((stored) => stored !== handle));

        try {
          window.localStorage.setItem(this.storageKey, JSON.stringify(history.slice(0, this.constructor.MAX_HISTORY)));
        } catch (error) {
          // Storage full or disabled: nothing to record, nothing to report.
        }
      }

      clearHistory() {
        try {
          window.localStorage.removeItem(this.storageKey);
        } catch (error) {
          // Nothing to clear if storage is unavailable.
        }
      }
    }
  );
}
